import React, { useEffect, useState, useRef } from 'react';
import { PlusOutlined, TeamOutlined } from '@ant-design/icons';
import { AutoComplete, Flex, Popconfirm, Tag, theme, Spin } from 'antd';
import { useStore } from '../../inbox-store';
import _ from 'lodash';

interface GroupOption {
  value: string;
  label: string;
  groupId?: string;
  isNew?: boolean;
}

const GroupsComponent: React.FC = () => {
  const { token } = theme.useToken();
  const [inputVisible, setInputVisible] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const [options, setOptions] = useState<GroupOption[]>([]);
  const [pendingGroup, setPendingGroup] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const autoCompleteRef = useRef<any>(null);

  const activeContactModel = useStore((state) => state.activeContactModel);
  const activeContact = useStore((state) => state.activeContact);
  const companyGroups = useStore((state) => state.companyGroups);
  const fetchCompanyGroups = useStore((state) => state.fetchCompanyGroups);
  const addContactToGroup = useStore((state) => state.addContactToGroup);
  const removeContactFromGroup = useStore((state) => state.removeContactFromGroup);
  const createGroupAndAddContact = useStore((state) => state.createGroupAndAddContact);

  const groups = activeContactModel?.groups || [];

  useEffect(() => {
    if (inputVisible) {
      fetchCompanyGroups();
      setTimeout(() => autoCompleteRef.current?.focus(), 50);
    }
  }, [inputVisible]);

  const handleSearch = (searchText: string) => {
    setInputValue(searchText);

    const memberGroupIds = groups.map((g) => g.id);
    const filtered = companyGroups
      .filter((g) => !memberGroupIds.includes(g.id))
      .filter((g) => g.name.toLowerCase().includes(searchText.toLowerCase()));

    const opts: GroupOption[] = filtered.map((g) => ({
      value: g.id,
      label: g.name,
      groupId: g.id,
    }));

    if (searchText.trim() && !companyGroups.some((g) => g.name.toLowerCase() === searchText.toLowerCase())) {
      opts.push({
        value: `new:${searchText}`,
        label: `Create "${searchText}"`,
        isNew: true,
      });
    }

    setOptions(opts);
  };

  const handleSelect = async (val: string, option: GroupOption) => {
    setLoading(true);
    setInputVisible(false);
    setInputValue('');

    if (option.isNew) {
      const name = val.replace('new:', '');
      await createGroupAndAddContact(name);
    } else {
      await addContactToGroup(val);
    }

    setLoading(false);
  };

  const handleRemoveGroup = async (groupId: string) => {
    setPendingGroup(null);
    await removeContactFromGroup(groupId);
  };

  const handleBlur = () => {
    setInputVisible(false);
    setInputValue('');
  };

  if (!activeContact || !activeContactModel?.id) return null;

  return (
    <Flex gap="4px 0" wrap align="center">
      {groups.length > 0 && (
        <TeamOutlined style={{ color: '#8c8c8c', fontSize: 12, marginRight: 4 }} />
      )}
      {groups.map((group) => (
        <Popconfirm
          key={group.id}
          title={`Remove from "${group.name}"?`}
          onConfirm={() => handleRemoveGroup(group.id)}
          onCancel={() => setPendingGroup(null)}
          placement="bottomLeft"
          okText="Yes"
          cancelText="No"
          open={pendingGroup === group.id}
        >
          <Tag
            closable
            style={{
              backgroundColor: '#F0F5FF',
              color: '#1D39C4',
              fontSize: 12,
              padding: '0 3px',
              borderRadius: 12,
              border: '1px solid #ADC6FF',
              cursor: 'default',
              margin: 2,
              userSelect: 'none',
            }}
            onMouseEnter={(e: any) => (e.currentTarget.style.backgroundColor = '#E6EDFF')}
            onMouseLeave={(e: any) => (e.currentTarget.style.backgroundColor = '#F0F5FF')}
            onClose={(e) => {
              e.preventDefault();
              setPendingGroup(group.id);
            }}
          >
            {group.name}
          </Tag>
        </Popconfirm>
      ))}
      {loading ? (
        <Spin size="small" />
      ) : inputVisible ? (
        <AutoComplete
          ref={autoCompleteRef}
          size="small"
          style={{ width: 140 }}
          options={options}
          onSearch={handleSearch}
          onSelect={handleSelect}
          onBlur={handleBlur}
          value={inputValue}
          placeholder="Search or create..."
        />
      ) : (
        <Tag
          style={{
            height: 22,
            background: token.colorBgContainer,
            borderStyle: 'dashed',
            cursor: 'pointer',
          }}
          icon={<PlusOutlined />}
          onClick={() => setInputVisible(true)}
        >
          Group
        </Tag>
      )}
    </Flex>
  );
};

export default GroupsComponent;
